import { Check, CreditCard, Loader2, Mail, Sparkles, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useUserProfile } from '../context/UserProfileContext'
import paymentService from '../utils/paymentService'
import { ApiError } from '../utils/api'
import GetInTouchModal from './GetInTouchModal'

interface PlanMetadata {
  customPricing?: boolean
  description?: string
  ctaLabel?: string
  ctaUrl?: string
}

interface PricingPlan {
  _id: string
  name: string
  slug: string
  productType: string
  price: number
  currency: string
  billingCycle: string
  features: string[]
  isActive: boolean
  sortOrder?: number
  metadata?: PlanMetadata
}

interface CT600PlanSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  formId: string
  /**
   * Company the payment is for. Sent to the backend so the resulting
   * subscription/transaction can be tagged with `companyId`. The coverage
   * check keys off this when the user comes back later. In the CT600 wizard
   * payment happens before the form is created, so `formId` may be empty.
   */
  companyId?: string
  onPlanSelected?: (slug: string) => void
  selectOnly?: boolean
  /**
   * When true, only show dormant-company plans (used when the user has
   * flagged the company as Dormant). When false (default) dormant plans
   * are hidden so the user picks from regular CT600 plans.
   */
  dormantOnly?: boolean
}

const isDormantPlanName = (name: string) =>
  name.toLowerCase().includes('dormant')

export default function CT600PlanSelectionModal({
  isOpen,
  onClose,
  formId,
  companyId,
  onPlanSelected,
  selectOnly = false,
  dormantOnly = false,
}: CT600PlanSelectionModalProps) {
  const { user } = useUserProfile()
  const [plans, setPlans] = useState<PricingPlan[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string | null>(null)
  // Plan name driving the "Get in touch" modal; null when it's closed.
  const [contactPlanName, setContactPlanName] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      fetchCT600Plans()
    }
  }, [isOpen])

  const fetchCT600Plans = async () => {
    try {
      setIsLoading(true)
      const response = await paymentService.listPricingPlans()

      const ct600Plans: PricingPlan[] =
        (response as any)?.ct600 || (response as any)?.['ct600'] || []

      const sortedPlans = ct600Plans.sort((a: PricingPlan, b: PricingPlan) => {
        if (a.sortOrder && b.sortOrder) {
          return a.sortOrder - b.sortOrder
        }
        return a.price - b.price
      })

      setPlans(sortedPlans)
    } catch (error) {
      console.error('Failed to fetch CT600 plans:', error)
      toast.error('Unable to load pricing plans. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Plans the user can choose right now, filtered by the dormant flag.
  const visiblePlans = useMemo(() => {
    return plans.filter(plan =>
      dormantOnly ? isDormantPlanName(plan.name) : !isDormantPlanName(plan.name)
    )
  }, [plans, dormantOnly])

  // Keep the highlighted plan inside the visible list. If the user toggles
  // dormant after a non-dormant plan was selected (or vice versa), reset to
  // the first visible plan.
  useEffect(() => {
    // Custom-pricing plans (contact-us) can't be checked out, so they're never
    // the default selection.
    const selectablePlans = visiblePlans.filter(p => !p.metadata?.customPricing)
    if (selectablePlans.length === 0) {
      setSelectedPlanSlug(null)
      return
    }
    const stillVisible = selectablePlans.some(p => p.slug === selectedPlanSlug)
    if (!stillVisible) {
      setSelectedPlanSlug(selectablePlans[0]!.slug)
    }
  }, [visiblePlans, selectedPlanSlug])

  const handlePlanSelection = (slug: string) => {
    setSelectedPlanSlug(slug)
  }

  const handleProceed = async () => {
    if (!selectedPlanSlug) {
      toast.error('Please select a plan to continue')
      return
    }

    // In selectOnly mode, just fire callback — no formId needed yet
    if (selectOnly) {
      if (onPlanSelected) {
        onPlanSelected(selectedPlanSlug)
      }
      toast.success('Plan selected successfully')
      onClose()
      return
    }

    // For checkout mode, formId is required
    if (!formId) {
      toast.error('Please select a plan to continue')
      return
    }

    try {
      setIsProcessing(true)

      const successUrl = `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&plan=${selectedPlanSlug}&formId=${formId}&source=ct600`
      const cancelUrl = `${window.location.origin}/history`

      const checkoutData = await paymentService.createCheckout(
        selectedPlanSlug,
        {
          successUrl,
          cancelUrl,
          customerEmail: user?.email || '',
          formId,
          // Pin the payment to this company so the coverage check finds it
          // when the user returns to edit the same company later.
          ...(companyId ? { companyId } : {}),
        }
      )

      if (!checkoutData.url) {
        throw new Error('Invalid checkout session URL')
      }

      window.location.href = checkoutData.url
    } catch (err) {
      console.error('Checkout failed:', err)
      if (err instanceof ApiError) {
        toast.error(
          err.message || 'Unable to start checkout. Please try again.'
        )
      } else {
        toast.error('Unable to start checkout. Please try again.')
      }
      setIsProcessing(false)
    }
  }

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
    }).format(price)
  }

  if (!isOpen) return null

  const userFullName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
    : ''

  return (
    <>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm'>
        <div className='bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col'>
          {/* Header */}
          <div className='px-4 sm:px-8 pt-5 sm:pt-8 pb-4'>
            <div className='flex items-start justify-between gap-3'>
              <div className='min-w-0 flex-1'>
                <div className='flex items-center gap-2.5'>
                  <div className='flex h-9 w-9 items-center justify-center rounded-lg bg-gray-900 flex-shrink-0'>
                    <Sparkles className='h-4.5 w-4.5 text-white' />
                  </div>
                  <h2 className='text-lg sm:text-xl font-bold text-gray-900'>
                    Choose Your Plan
                  </h2>
                </div>
                <p className='mt-2 text-xs sm:text-sm text-gray-500'>
                  Select a Corporation Tax filing plan to continue
                </p>
              </div>
              <button
                onClick={onClose}
                disabled={isProcessing}
                className='rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50 flex-shrink-0'
              >
                <X className='h-5 w-5' />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className='flex-1 overflow-y-auto px-4 sm:px-8 py-4'>
            {isLoading ? (
              <div className='flex flex-col items-center justify-center py-16'>
                <Loader2 className='h-8 w-8 animate-spin text-gray-900' />
                <p className='mt-3 text-sm text-gray-500'>Loading plans...</p>
              </div>
            ) : visiblePlans.length === 0 ? (
              <div className='text-center py-16'>
                <p className='text-gray-500'>
                  {dormantOnly
                    ? 'No dormant company plans available at the moment.'
                    : 'No Corporation Tax plans available at the moment.'}
                </p>
                <button
                  onClick={onClose}
                  className='mt-4 text-sm font-medium text-gray-900 underline underline-offset-4 hover:text-gray-700'
                >
                  Close
                </button>
              </div>
            ) : (
              <div className='space-y-3'>
                {visiblePlans.map(plan => {
                  // Custom-pricing plans aren't selectable for checkout — render a
                  // non-interactive card with a "get in touch" link instead.
                  if (plan.metadata?.customPricing) {
                    return (
                      <div
                        key={plan.slug}
                        className='relative w-full rounded-xl border-2 border-gray-200 bg-gray-50/50 p-4 sm:p-5'
                      >
                        <h3 className='text-sm sm:text-base font-semibold text-gray-900'>
                          {plan.name}
                        </h3>
                        {plan.metadata.description && (
                          <p className='mt-1.5 text-xs sm:text-sm text-gray-500'>
                            {plan.metadata.description}
                          </p>
                        )}

                        {/* Features */}
                        <ul className='mt-3 space-y-1.5'>
                          {plan.features.map((feature, index) => (
                            <li
                              key={index}
                              className='flex items-start gap-2 text-xs sm:text-sm text-gray-600'
                            >
                              <Check className='h-4 w-4 text-gray-900 mt-0.5 flex-shrink-0' />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>

                        <button
                          type='button'
                          onClick={() => setContactPlanName(plan.name)}
                          className='mt-4 inline-flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors'
                        >
                          <Mail className='h-4 w-4' />
                          {plan.metadata.ctaLabel || 'Get in touch'}
                        </button>
                      </div>
                    )
                  }

                  const isSelected = selectedPlanSlug === plan.slug
                  return (
                    <button
                      key={plan.slug}
                      type='button'
                      onClick={() => handlePlanSelection(plan.slug)}
                      className={`relative w-full text-left rounded-xl border-2 p-4 sm:p-5 transition-all ${
                        isSelected
                          ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50'
                      }`}
                    >
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex items-center gap-3'>
                            <h3 className='text-sm sm:text-base font-semibold text-gray-900'>
                              {plan.name}
                            </h3>
                          </div>
                          <div className='mt-1.5 flex items-baseline gap-1.5 flex-wrap'>
                            <span className='text-xl sm:text-2xl font-bold text-gray-900'>
                              {formatPrice(plan.price, plan.currency)}
                            </span>
                            <span className='text-xs text-gray-500'>
                              {plan.billingCycle}
                            </span>
                          </div>

                          {/* Features */}
                          <ul className='mt-3 space-y-1.5'>
                            {plan.features.map((feature, index) => (
                              <li
                                key={index}
                                className='flex items-start gap-2 text-xs sm:text-sm text-gray-600'
                              >
                                <Check className='h-4 w-4 text-gray-900 mt-0.5 flex-shrink-0' />
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Selection indicator */}
                        <div
                          className={`mt-1 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full transition-colors ${
                            isSelected
                              ? 'bg-gray-900'
                              : 'border-2 border-gray-300'
                          }`}
                        >
                          {isSelected && (
                            <Check className='h-3 w-3 text-white' />
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Info note */}
            {visiblePlans.length > 0 && (
              <div className='mt-5 rounded-lg bg-gray-50 border border-gray-200 px-4 py-3'>
                <p className='text-xs text-gray-500'>
                  {selectOnly
                    ? 'Select your plan now. Payment will be required when you submit the form to HMRC.'
                    : 'Payment is required to complete and submit your Corporation Tax form (CT600) to HMRC. Your form data is saved and will be available after payment.'}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {visiblePlans.length > 0 && (
            <div className='border-t border-gray-200 px-4 sm:px-8 py-4 sm:py-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3'>
              <button
                onClick={onClose}
                disabled={isProcessing}
                className='rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                Cancel
              </button>
              <button
                onClick={handleProceed}
                disabled={!selectedPlanSlug || isProcessing}
                className='flex items-center justify-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isProcessing ? (
                  <>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Processing...
                  </>
                ) : selectOnly ? (
                  <>
                    <Check className='h-4 w-4' />
                    Select Plan
                  </>
                ) : (
                  <>
                    <CreditCard className='h-4 w-4' />
                    Continue to Payment
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <GetInTouchModal
        isOpen={contactPlanName !== null}
        onClose={() => setContactPlanName(null)}
        defaultEmail={user?.email || ''}
        defaultName={userFullName}
        subject={contactPlanName || undefined}
      />
    </>
  )
}
